/**
 * ステータス表示ユーティリティ
 * 案件ステータスの統一した日本語表示とスタイリングを提供
 *
 * FL-142: 案件ステータス機能追加 - 8状態に拡張
 */

import { Case } from '../types';

/**
 * 案件ステータスを日本語テキストに変換
 *
 * 対応ステータス（8値）:
 * - provisional: 仮受（電話・LINE相談段階）
 * - confirmed: 本受・未配車（正式依頼書受領後）
 * - assigned: 配車済み
 * - picked_up: 引き取り済み
 * - in_transit: 輸送中
 * - delivered: 搬入済み
 * - reported: 報告済み（作業完了報告提出済み）
 * - completed: 完了
 */
export const getCaseStatusText = (status: Case['status']): string => {
  // データベースから取得したstatusがnullまたはundefinedになる場合があるため、ガード句で未設定を返す
  if (!status) {
    return '未設定';
  }

  // データベースの小文字値とTypeScript型の大文字値両方に対応
  const normalizedStatus = status.toLowerCase();
  switch (normalizedStatus) {
    // 新規ステータス（FL-142）
    case 'provisional':
      return '仮受';
    case 'confirmed':
      return '本受・未配車';
    case 'picked_up':
    case 'picked-up':
      return '引き取り済み';
    case 'delivered':
      return '搬入済み';
    case 'reported':
      return '報告済み';

    // 既存ステータス（後方互換性）
    case 'unassigned':
      return '未割当';
    case 'assigned':
      return '配車済み';
    case 'in_transit':
    case 'in-transit':
      return '輸送中';
    case 'completed':
      return '完了';

    default:
      return status;
  }
};

/**
 * 案件ステータスのTailwind CSSクラスを取得
 *
 * 色分けルール（FL-142）:
 * - 仮受段階（provisional, confirmed, assigned）: 薄い色
 *   * provisional: 薄い黄色（相談中）
 *   * confirmed: 薄い青色（依頼確定）
 *   * assigned: 薄い紫色（配車済み）
 *
 * - 作業中段階（picked_up, in_transit, delivered, reported）: 濃い色
 *   * picked_up: 濃いオレンジ（引き取り開始）
 *   * in_transit: 濃いインディゴ（輸送中）
 *   * delivered: 濃いグリーン（搬入完了）
 *   * reported: 濃いシアン（報告完了）
 *
 * - 最終段階（completed）: 濃い緑色
 */
export const getCaseStatusClass = (status: Case['status']): string => {
  if (!status) {
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }

  // データベースの小文字値とTypeScript型の大文字値両方に対応
  const normalizedStatus = status.toLowerCase();
  switch (normalizedStatus) {
    // 新規ステータス（FL-142）
    case 'provisional':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    case 'confirmed':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'picked_up':
    case 'picked-up':
      return 'bg-orange-500 text-white dark:bg-orange-600 dark:text-white';
    case 'delivered':
      return 'bg-green-500 text-white dark:bg-green-600 dark:text-white';
    case 'reported':
      return 'bg-cyan-500 text-white dark:bg-cyan-600 dark:text-white';

    // 既存ステータス（後方互換性）
    case 'unassigned':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    case 'assigned':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    case 'in_transit':
    case 'in-transit':
      return 'bg-indigo-500 text-white dark:bg-indigo-600 dark:text-white';
    case 'completed':
      return 'bg-green-600 text-white dark:bg-green-700 dark:text-white';

    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

/**
 * ドラッグ&ドロップ操作用のケース背景クラスを取得
 *
 * スケジュール画面用の背景色を提供
 * ステータスに応じた視覚的な区分
 *
 * 色分けルール（FL-142）:
 * - provisional（仮受）: 薄い黄色 - 相談中であることを強調
 * - confirmed（本受・未配車）: 薄い青色 - 対応予定であることを強調
 * - assigned（配車済み）: 薄い紫色 - 陸送員が決定していることを強調
 * - picked_up（引き取り済み）: 濃いオレンジ - 作業が開始したことを強調
 * - in_transit（輸送中）: 濃いインディゴ - アクティブな作業を強調
 * - delivered（搬入済み）: 濃いグリーン - 重要なマイルストーン
 * - reported（報告済み）: 濃いシアン - 完了が近いことを強調
 * - completed（完了）: 濃いグリーン - 完了したことを強調
 *
 * acceptanceStatusが指定される場合（従来システム互換性）:
 * - provisional + unassigned: 薄い黄色
 * - provisional + assigned: 薄い青色
 * - confirmed + unassigned: 濃い赤色
 * - confirmed + assigned: 濃い青色・緑色
 *
 * acceptanceStatusが未指定の場合は割当状況で判定（フォールバック）:
 * - unassigned: 薄い赤色
 * - assigned: 薄い青色
 */
export const getDraggableCaseStatusClass = (
  status: Case['status'],
  assignedTransporterId?: string | null,
  acceptanceStatus?: 'provisional' | 'confirmed'
): string => {
  // acceptanceStatusが指定されている場合（従来システム互換性）
  if (acceptanceStatus) {
    const hasAssignment = assignedTransporterId !== undefined && assignedTransporterId !== null;

    if (acceptanceStatus === 'provisional') {
      // 仮受段階
      if (hasAssignment) {
        // 仮受 + 割当済 → 薄い青色
        return 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20';
      } else {
        // 仮受 + 未割当 → 薄い黄色
        return 'border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20';
      }
    } else if (acceptanceStatus === 'confirmed') {
      // 本受段階
      if (hasAssignment) {
        // 本受 + 割当済 → 濃い青色・緑色
        return 'border-green-600 bg-green-600 text-white dark:border-green-700 dark:bg-green-700';
      } else {
        // 本受 + 未割当 → 濃い赤色
        return 'border-red-600 bg-red-600 text-white dark:border-red-700 dark:bg-red-700';
      }
    }
  }

  // acceptanceStatusが未指定で、かつassignedTransporterIdが明示的に指定された場合は割当状況で判定（フォールバック）
  if (!acceptanceStatus && assignedTransporterId !== undefined) {
    const hasAssignment = assignedTransporterId !== null;
    if (hasAssignment) {
      // 割当済 → 薄い青色
      return 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20';
    } else {
      // 未割当 → 薄い赤色
      return 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20';
    }
  }

  // statusがundefined/nullの場合はデフォルト色
  if (!status) {
    return 'border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/20';
  }

  // statusに基づいて色を決定（新規8状態対応）
  const normalizedStatus = status.toLowerCase();
  switch (normalizedStatus) {
    // 仮受段階 - 薄い色
    case 'provisional':
      return 'border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20';
    case 'confirmed':
      return 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20';
    case 'assigned':
    case 'unassigned':
      return 'border-purple-300 bg-purple-50 dark:border-purple-700 dark:bg-purple-900/20';

    // 作業中段階 - 濃い色
    case 'picked_up':
    case 'picked-up':
      return 'border-orange-500 bg-orange-500 text-white dark:border-orange-600 dark:bg-orange-600';
    case 'in_transit':
    case 'in-transit':
      return 'border-indigo-500 bg-indigo-500 text-white dark:border-indigo-600 dark:bg-indigo-600';
    case 'delivered':
      return 'border-green-500 bg-green-500 text-white dark:border-green-600 dark:bg-green-600';
    case 'reported':
      return 'border-cyan-500 bg-cyan-500 text-white dark:border-cyan-600 dark:bg-cyan-600';

    // 完了段階
    case 'completed':
      return 'border-green-600 bg-green-600 text-white dark:border-green-700 dark:bg-green-700';

    default:
      return 'border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/20';
  }
};

/**
 * 指定されたステータスから遷移可能なステータス一覧を取得
 *
 * ステータス遷移ルール:
 * - provisional → confirmed, assigned
 * - confirmed → assigned, picked_up
 * - assigned → picked_up, in_transit
 * - picked_up → in_transit
 * - in_transit → delivered
 * - delivered → reported
 * - reported → completed
 * - completed → (遷移不可)
 */
export const getAvailableTransitions = (currentStatus: Case['status']): Case['status'][] => {
  switch (currentStatus) {
    case 'provisional':
      return ['confirmed', 'assigned'];
    case 'confirmed':
      return ['assigned', 'picked_up'];
    case 'assigned':
      return ['picked_up', 'in_transit'];
    case 'picked_up':
      return ['in_transit'];
    case 'in_transit':
      return ['delivered'];
    case 'delivered':
      return ['reported'];
    case 'reported':
      return ['completed'];
    case 'completed':
      return []; // 完了後は遷移不可
    default:
      return [];
  }
};
